import React, { createContext, ReactNode, useReducer, useEffect } from 'react';
import { getLogger } from '@packages/core/logging';
import { ContentSettingDashboard, Techline } from '@packages/models/api';
import { ContentSettingsService } from './content-settings.service';
import {
    dashboardReducer,
    initialState,
    dashboardActions,
    DashboardState,
    DashboardActionDispatcher,
} from './content-settings.state';

const logger = getLogger('content-settings');

type DashboardSection = keyof ContentSettingDashboard;

interface ContentSettingsContextType {
    state: DashboardState;
    dispatch: DashboardActionDispatcher;
    loadDashboards: () => Promise<void>;
    addDashboard: <T extends DashboardSection>(section: T, dashboard: Techline) => Promise<void>;
    updateDashboard: <T extends DashboardSection>(
        section: T,
        id: string,
        dashboard: Partial<Techline>
    ) => Promise<void>;
    deleteDashboard: <T extends DashboardSection>(section: T, id: string) => Promise<void>;
    service: ContentSettingsService;
}

export const ContentSettingsContext = createContext<ContentSettingsContextType | undefined>(undefined);

export const ContentSettingsProvider: React.FC<{ children: ReactNode; service: ContentSettingsService }> = ({
    children,
    service,
}) => {
    const [state, dispatch] = useReducer(dashboardReducer, initialState);

    const loadDashboards = async () => {
        dispatch(dashboardActions.isLoading());
        try {
            const data = await service.fetchDashboard();
            dispatch(dashboardActions.setDashboardData({ dashboardData: data }));
            dispatch(dashboardActions.hasLoaded());
        } catch (error) {
            logger.error('Failed to load the content settings dashboards', { error });
            const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
            dispatch(dashboardActions.hasErrors({ errors: [errorMessage] }));
        }
    };

    const addDashboard = async <T extends DashboardSection>(section: T, dashboard: Techline) => {
        const optimisticDashboard = { ...dashboard, techlineDashboardId: `${Date.now()}` };

        dispatch(
            dashboardActions.setDashboardData({
                dashboardData: {
                    ...state.dashboardData,
                    [section]: [...(state.dashboardData[section] || []), optimisticDashboard],
                },
            })
        );

        try {
            const newDashboard = (await service.createDashboard(dashboard)) as { techline: Techline };
            const newSection = newDashboard?.[section];
            dispatch(
                dashboardActions.setDashboardData({
                    dashboardData: {
                        ...state.dashboardData,
                        [section]: [...(state.dashboardData[section] || []), newSection],
                    },
                })
            );
        } catch (error) {
            logger.error(`Failed to create the dashboard in section [${section}]`, { error, section });
            dispatch(
                dashboardActions.setDashboardData({
                    dashboardData: {
                        ...state.dashboardData,
                        [section]:
                            state.dashboardData[section]?.filter(
                                (d) => (d as Techline).techlineDashboardId !== optimisticDashboard.techlineDashboardId
                            ) || [],
                    },
                })
            );
        }
    };

    const updateDashboard = async <T extends DashboardSection>(
        section: T,
        id: string,
        dashboard: Partial<Techline>
    ) => {
        dispatch(
            dashboardActions.setDashboardData({
                dashboardData: {
                    ...state.dashboardData,
                    [section]:
                        state.dashboardData[section]?.map((d) =>
                            (d as Techline).techlineDashboardId === id ? { ...d, ...dashboard } : d
                        ) || [],
                },
            })
        );

        try {
            await service.updateDashboard(id, dashboard);
        } catch (error) {
            logger.error(`Failed to update the dashboard [${id}]`, { dashboardId: id, error, section });
            loadDashboards();
        }
    };

    const deleteDashboard = async <T extends DashboardSection>(section: T, id: string) => {
        const previousDashboards = { ...state.dashboardData };

        dispatch(
            dashboardActions.setDashboardData({
                dashboardData: {
                    ...state.dashboardData,
                    [section]:
                        state.dashboardData[section]?.filter((d) => (d as Techline).techlineDashboardId !== id) || [],
                },
            })
        );

        try {
            await service.deleteDashboard(id);
        } catch (error) {
            logger.error(`Failed to delete the dashboard [${id}]`, { dashboardId: id, error, section });
            dispatch(dashboardActions.setDashboardData({ dashboardData: previousDashboards })); // Rollback
        }
    };

    useEffect(() => {
        loadDashboards();
    }, []);

    return (
        <ContentSettingsContext.Provider
            value={{
                state,
                dispatch,
                loadDashboards,
                addDashboard,
                updateDashboard,
                deleteDashboard,
                service,
            }}
        >
            {children}
        </ContentSettingsContext.Provider>
    );
};
